export enum EmailType {
  distributorCreated = 'Distributor Created',
  batchPaymentFailed = 'Batch Payment Failed',
  batchPaymentReceipt = 'Batch Payment Receipt',
  distributorTeamInvite = 'Distributor Team Invite',
  distributorTeamMemberAdded = 'Distributor Team Member Added',
}

export enum DeliveryMode {
  EVENT_DRIVEN = 'EVENT_DRIVEN',
}

export interface EmailToken {
  token: string;
  description: string;
  example: string;
}

export interface Email {
  readonly id: string;
  enabled: boolean;

  readonly created: Date;
  readonly modified: Date;

  type: EmailType;
  deliveryMode: DeliveryMode;
  templateLabel?: string | null;

  subject: string | null;
  content: string | null;
  byline: string | null;
}

export interface CreateEmailTemplateRequest {
  type: EmailType;
  templateLabel: string | null;
  subject: string | null;
  content: string | null;
  byline: string | null;
}

export interface CreateEmailTemplateResponse {
  id: string;
}

export interface UpdateEmailTemplateRequest {
  enabled?: boolean;
  type?: EmailType;
  templateLabel?: string | null;
  subject?: string | null;
  content?: string | null;
  byline?: string | null;
}

export interface UpdateEmailTemplateResponse {
  id: string;
}

const DISTRIBUTOR_TEAM_TOKENS: EmailToken[] = [
  {
    token: '{{recipientName}}',
    description: 'Name of the invited team member.',
    example: 'Jordan Lee',
  },
  {
    token: '{{distributorName}}',
    description: 'Name of the distributor the team member joins.',
    example: 'Acme Home Inspections',
  },
  {
    token: '{{productTeam}}',
    description: 'Product or team name shown in outgoing communication.',
    example: 'Warranty Sphere',
  },
];

export const EMAIL_TEMPLATE_TOKENS: Partial<Record<EmailType, EmailToken[]>> = {
  [EmailType.distributorCreated]: [
    {
      token: '{{recipientName}}',
      description: 'Name of the email recipient.',
      example: 'Product team',
    },
    {
      token: '{{recipientEmail}}',
      description: 'Email address of the recipient.',
      example: 'product@example.com',
    },
    {
      token: '{{brandName}}',
      description: 'Brand name shown in outgoing communication.',
      example: 'Warranty Sphere',
    },
    {
      token: '{{distributorId}}',
      description: 'Unique identifier of the created distributor.',
      example: 'dist_123456',
    },
    {
      token: '{{distributorName}}',
      description: 'Name of the distributor that was created.',
      example: 'Acme Home Inspections',
    },
    {
      token: '{{distributorEmail}}',
      description: 'Primary email configured on the distributor.',
      example: 'ops@acme.com',
    },
    {
      token: '{{distributorPhone}}',
      description: 'Primary phone number configured on the distributor.',
      example: '+1 555 123 4567',
    },
    {
      token: '{{dashboardUrl}}',
      description: 'Base dashboard URL.',
      example: 'https://dashboard.example.com',
    },
    {
      token: '{{distributorUrl}}',
      description: 'Direct dashboard URL for the created distributor.',
      example: 'https://dashboard.example.com/distributors/dist_123456',
    },
  ],
  [EmailType.batchPaymentFailed]: [
    {
      token: '{{recipientName}}',
      description: 'Name of the email recipient.',
      example: 'Acme Home Inspections',
    },
    {
      token: '{{distributorName}}',
      description: 'Name of the distributor whose batch payment failed.',
      example: 'Acme Home Inspections',
    },
    {
      token: '{{dashboardUrl}}',
      description: 'Base dashboard URL.',
      example: 'https://dashboard.example.com',
    },
    {
      token: '{{distributorUrl}}',
      description: 'Direct dashboard URL for the distributor, where the payment can be retried.',
      example: 'https://dashboard.example.com/distributors/dist_123456',
    },
  ],
  [EmailType.batchPaymentReceipt]: [
    {
      token: '{{recipientName}}',
      description: 'Name of the email recipient.',
      example: 'Acme Home Inspections',
    },
    {
      token: '{{distributorName}}',
      description: 'Name of the distributor that completed the batch payment.',
      example: 'Acme Home Inspections',
    },
    {
      token: '{{transactionDate}}',
      description: 'Date the payment settled.',
      example: 'August 19, 2026',
    },
    {
      token: '{{referenceNumber}}',
      description: "The payment gateway's transaction reference.",
      example: '120088749424',
    },
    {
      token: '{{paymentMethod}}',
      description: 'Payment method used, masked to the last four digits.',
      example: 'AmericanExpress •••• 0002',
    },
    {
      token: '{{warrantyCount}}',
      description: 'Number of warranties funded by the batch.',
      example: '4',
    },
    {
      token: '{{totalPaid}}',
      description: 'Total amount charged.',
      example: '$200.00',
    },
  ],
  [EmailType.distributorTeamInvite]: DISTRIBUTOR_TEAM_TOKENS,
  [EmailType.distributorTeamMemberAdded]: DISTRIBUTOR_TEAM_TOKENS,
};
